import {
  gameTime,
  gameTimeV2,
  getGameDateTime,
  getVolumeBaseDate,
  setGameTime,
  setGameTimeV2,
  startGameClock,
} from "@paulie/game-utils";
import {
  Component,
  createSignal,
  JSX,
  onCleanup,
  onMount,
  Show,
} from "solid-js";
import { MachineSwitcher, type MachineInfo } from "../MachineSwitcher";

/**
 * Theme variants for the DesktopPanel
 */
export type DesktopPanelTheme = "volume1" | "volume2";

/**
 * Props for the DesktopPanel component
 */
export interface DesktopPanelProps {
  /** Machine/terminal name shown on left (e.g., "chen-dev-station") */
  machine: string;
  /** Optional centered title */
  title?: string;
  /** Tray items - JSX for toolbar buttons, badges, etc. */
  tray?: JSX.Element;
  /** Optional close handler - if provided, dots become clickable close button */
  onClose?: () => void;
  /** Panel content */
  children: JSX.Element;
  /** Optional class for the outer container */
  class?: string;
  /** Optional list of machines for the machine switcher */
  machines?: MachineInfo[];
  /** Optional callback for when a machine is selected */
  onMachineSelect?: (route: string) => void;
  /** Theme variant - volume1 (amber) or volume2 (purple/cyan). Default: volume1 */
  theme?: DesktopPanelTheme;
}

/**
 * Theme-specific class names, keyed by variant.
 *
 * A lookup table rather than a branch: both variants carry the same ten keys,
 * so a table makes a missing key a type error instead of a silent mismatch.
 */
const THEME_CLASSES: Record<DesktopPanelTheme, Record<string, string>> = {
  volume1: {
    border: "border-amber/20",
    indicator: "text-amber",
    accentText: "text-amber",
    accentHover: "hover:text-amber",
    accentBorder: "border-amber/30",
    accentBorderHover: "hover:border-amber/50",
    accentBg: "bg-amber/10",
    accentBgHover: "hover:bg-amber/20",
    focusBorder: "focus:border-amber",
    focusRing: "focus:ring-amber/50",
  },
  volume2: {
    border: "border-purple-500/20",
    indicator: "text-purple-400",
    accentText: "text-purple-400",
    accentHover: "hover:text-purple-400",
    accentBorder: "border-purple-500/30",
    accentBorderHover: "hover:border-purple-500/50",
    accentBg: "bg-purple-500/10",
    accentBgHover: "hover:bg-purple-500/20",
    focusBorder: "focus:border-purple-400",
    focusRing: "focus:ring-purple-400/50",
  },
};

/**
 * DesktopPanel - Terminal-style panel with title bar and content area
 *
 * A reusable panel component with a terminal aesthetic featuring:
 * - Machine name with animated indicator
 * - Optional centered title
 * - Tray slot for toolbar items
 * - Real-time date/time display
 * - Optional close button
 *
 * @example
 * <DesktopPanel
 *   machine={CHARACTERS.B7_DEV?.machineName ?? "workstation"}
 *   title="System Diagnostics"
 *   tray={<Badge>Active</Badge>}
 *   onClose={() => console.log("closed")}
 * >
 *   <div>Panel content here</div>
 * </DesktopPanel>
 */
export const DesktopPanel: Component<DesktopPanelProps> = (props) => {
  const [showDatePicker, setShowDatePicker] = createSignal(false);

  // Get theme-specific classes
  const themeClasses = () => THEME_CLASSES[props.theme ?? "volume1"];

  // Volume-aware game time derivations
  const isVolume2 = () => props.theme === "volume2";
  const currentGameTime = () => (isVolume2() ? gameTimeV2() : gameTime());
  const updateGameTime = (date: Temporal.ZonedDateTime) =>
    isVolume2() ? setGameTimeV2(date) : setGameTime(date);
  // The volume's default date, from the single source of truth in game-utils
  // rather than a second set of constants kept in sync by hand.
  const baseDate = () => getVolumeBaseDate(isVolume2() ? 2 : 1);
  const resetDateLabel = () =>
    baseDate().toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  // Derived from the shared clock signal, so it re-renders on every tick and
  // immediately reflects a date chosen in the picker.
  const dateTime = () =>
    getGameDateTime(isVolume2() ? { volume: 2 } : undefined);

  // Date picker form values
  const currentDate = () => currentGameTime();
  const [year, setYear] = createSignal(currentDate().year);
  const [month, setMonth] = createSignal(currentDate().month);
  const [day, setDay] = createSignal(currentDate().day);

  let datePickerRef: HTMLDivElement | undefined;

  onMount(() => {
    onCleanup(startGameClock());
  });

  onCleanup(() => {
    document.removeEventListener("click", handleClickOutside);
  });

  const handleClose = () => props.onClose?.();

  const handleClickOutside = (e: MouseEvent) => {
    if (
      datePickerRef &&
      e.target instanceof Node &&
      !datePickerRef.contains(e.target)
    ) {
      setShowDatePicker(false);
    }
  };

  const handleToggleDatePicker = () => {
    const newState = !showDatePicker();

    if (newState) {
      // Reset form to current game time
      const current = currentGameTime();
      setYear(current.year);
      setMonth(current.month);
      setDay(current.day);

      // Add click outside listener on next tick to avoid immediate close
      setTimeout(() => {
        document.addEventListener("click", handleClickOutside);
      }, 0);
    } else {
      document.removeEventListener("click", handleClickOutside);
    }

    setShowDatePicker(newState);
  };

  const handleSaveDate = () => {
    // `.with` preserves the time of day; only the date fields change.
    updateGameTime(
      currentGameTime().with({ year: year(), month: month(), day: day() })
    );
    setShowDatePicker(false);
    document.removeEventListener("click", handleClickOutside);
  };

  const handleCancelDate = () => {
    setShowDatePicker(false);
    document.removeEventListener("click", handleClickOutside);
  };

  const handleResetDate = () => {
    // Reset to the volume's default date, preserving time of day
    updateGameTime(
      currentGameTime().with({
        year: baseDate().year,
        month: baseDate().month,
        day: baseDate().day,
      })
    );
    // Also update the form spinners to reflect the reset
    setYear(baseDate().year);
    setMonth(baseDate().month);
    setDay(baseDate().day);
    setShowDatePicker(false);
    document.removeEventListener("click", handleClickOutside);
  };

  // Get the number of days in a given month
  const getDaysInMonth = (y: number, m: number) => new Date(y, m, 0).getDate();

  // Clamp day to valid range for current month
  const clampDay = (y: number, m: number, d: number) =>
    Math.min(d, getDaysInMonth(y, m));

  // Spinner increment/decrement handlers with wrapping
  const incrementYear = () => {
    const newYear = year() + 1;
    setYear(newYear);
    setDay((d) => clampDay(newYear, month(), d));
  };
  const decrementYear = () => {
    const newYear = year() - 1;
    setYear(newYear);
    setDay((d) => clampDay(newYear, month(), d));
  };

  const incrementMonth = () => {
    const newMonth = month() >= 12 ? 1 : month() + 1;
    setMonth(newMonth);
    setDay((d) => clampDay(year(), newMonth, d));
  };
  const decrementMonth = () => {
    const newMonth = month() <= 1 ? 12 : month() - 1;
    setMonth(newMonth);
    setDay((d) => clampDay(year(), newMonth, d));
  };

  // Typed entry goes through the same clamping as the spinner buttons.
  // Previously each input hand-rolled a looser bound — the year had none and
  // never re-clamped the day, and the day clamped to a flat 31 regardless of
  // month — so typing could produce Feb 29 in a common year, or Apr 31.
  const clampToMonth = (y: number, m: number) =>
    setDay((d) => clampDay(y, m, d));

  const applyYear = (raw: string) => {
    const nextYear = parseInt(raw) || baseDate().year;
    setYear(nextYear);
    clampToMonth(nextYear, month());
  };

  const applyMonth = (raw: string) => {
    const nextMonth = Math.max(1, Math.min(12, parseInt(raw) || 1));
    setMonth(nextMonth);
    clampToMonth(year(), nextMonth);
  };

  const applyDay = (raw: string) => {
    const requested = Math.max(1, parseInt(raw) || 1);
    setDay(clampDay(year(), month(), requested));
  };

  const maxDayForCurrentMonth = () => getDaysInMonth(year(), month());
  const incrementDay = () =>
    setDay((d) => (d >= maxDayForCurrentMonth() ? 1 : d + 1));
  const decrementDay = () =>
    setDay((d) => (d <= 1 ? maxDayForCurrentMonth() : d - 1));

  return (
    <div class={props.class}>
      {/* Title bar */}
      <div
        class={`bg-whitesmoke/5 flex items-center justify-between gap-2 rounded-t-lg border px-2 py-2 sm:gap-4 sm:px-4 ${themeClasses().border}`}
      >
        {/* Left: Machine name or MachineSwitcher */}
        <Show
          when={props.machines && props.onMachineSelect}
          fallback={
            <div class="text-whitesmoke/40 flex max-w-[180px] items-center gap-1 font-mono text-xs sm:max-w-none sm:gap-2 sm:text-sm">
              <span
                class={`flex-shrink-0 animate-pulse ${themeClasses().indicator}`}
              >
                ◇
              </span>
              <span class="min-w-0 truncate">{props.machine}</span>
            </div>
          }
        >
          <MachineSwitcher
            currentMachine={props.machine}
            machines={props.machines!}
            onMachineSelect={props.onMachineSelect!}
          />
        </Show>

        {/* Center: Title */}
        <Show when={props.title}>
          <div class="text-whitesmoke/60 min-w-0 flex-1 truncate text-center font-mono text-sm">
            [{props.title}]
          </div>
        </Show>

        {/* Right: Tray, Date/Time, Dots */}
        <div class="text-whitesmoke/40 flex items-center gap-1 font-mono text-xs sm:gap-3 sm:text-sm">
          {/* Tray slot - hides at small breakpoints */}
          <Show when={props.tray}>
            <div class="hidden flex-shrink-0 items-center gap-0.5 sm:gap-2 md:flex">
              {props.tray}
            </div>
          </Show>

          {/* Date/Time display with dropdown picker */}
          <div
            class="relative hidden flex-shrink lg:inline"
            ref={datePickerRef}
          >
            <button
              onClick={handleToggleDatePicker}
              class={`text-whitesmoke/40 cursor-pointer transition-colors ${themeClasses().accentHover}`}
              aria-label="Adjust game time"
              title="Click to adjust game time"
            >
              {dateTime()}
            </button>

            {/* Date Picker Dropdown */}
            <Show when={showDatePicker()}>
              <div class="absolute top-full right-0 z-50 mt-2 min-w-[280px]">
                <div
                  class={`bg-charcoal/95 rounded-lg border shadow-2xl backdrop-blur-sm ${themeClasses().border}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Content */}
                  <div class="space-y-3 p-3">
                    {/* Horizontal spinner row */}
                    <div class="flex items-start justify-center gap-4">
                      {/* Year Spinner */}
                      <div class="flex flex-col items-center gap-1">
                        <button
                          onClick={incrementYear}
                          class={`text-whitesmoke/40 h-5 w-8 transition-colors ${themeClasses().accentHover}`}
                          aria-label="Increment year"
                          type="button"
                        >
                          ▲
                        </button>
                        <input
                          type="number"
                          value={year()}
                          onInput={(e) => applyYear(e.currentTarget.value)}
                          class={`bg-charcoal/70 w-16 [appearance:textfield] rounded border px-2 py-1 text-center font-mono text-sm transition-colors focus:ring-1 focus:outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${themeClasses().accentBorder} ${themeClasses().accentText} ${themeClasses().focusBorder} ${themeClasses().focusRing}`}
                          aria-label="Year"
                        />
                        <button
                          onClick={decrementYear}
                          class={`text-whitesmoke/40 h-5 w-8 transition-colors ${themeClasses().accentHover}`}
                          aria-label="Decrement year"
                          type="button"
                        >
                          ▼
                        </button>
                        <span class="text-whitesmoke/60 mt-0.5 font-mono text-xs tracking-wider">
                          YEAR
                        </span>
                      </div>

                      {/* Month Spinner */}
                      <div class="flex flex-col items-center gap-1">
                        <button
                          onClick={incrementMonth}
                          class={`text-whitesmoke/40 h-5 w-8 transition-colors ${themeClasses().accentHover}`}
                          aria-label="Increment month"
                          type="button"
                        >
                          ▲
                        </button>
                        <input
                          type="number"
                          value={month()}
                          onInput={(e) => applyMonth(e.currentTarget.value)}
                          min="1"
                          max="12"
                          class={`bg-charcoal/70 w-12 [appearance:textfield] rounded border px-2 py-1 text-center font-mono text-sm transition-colors focus:ring-1 focus:outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${themeClasses().accentBorder} ${themeClasses().accentText} ${themeClasses().focusBorder} ${themeClasses().focusRing}`}
                          aria-label="Month"
                        />
                        <button
                          onClick={decrementMonth}
                          class={`text-whitesmoke/40 h-5 w-8 transition-colors ${themeClasses().accentHover}`}
                          aria-label="Decrement month"
                          type="button"
                        >
                          ▼
                        </button>
                        <span class="text-whitesmoke/60 mt-0.5 font-mono text-xs tracking-wider">
                          MONTH
                        </span>
                      </div>

                      {/* Day Spinner */}
                      <div class="flex flex-col items-center gap-1">
                        <button
                          onClick={incrementDay}
                          class={`text-whitesmoke/40 h-5 w-8 transition-colors ${themeClasses().accentHover}`}
                          aria-label="Increment day"
                          type="button"
                        >
                          ▲
                        </button>
                        <input
                          type="number"
                          value={day()}
                          onInput={(e) => applyDay(e.currentTarget.value)}
                          min="1"
                          max="31"
                          class={`bg-charcoal/70 w-12 [appearance:textfield] rounded border px-2 py-1 text-center font-mono text-sm transition-colors focus:ring-1 focus:outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${themeClasses().accentBorder} ${themeClasses().accentText} ${themeClasses().focusBorder} ${themeClasses().focusRing}`}
                          aria-label="Day"
                        />
                        <button
                          onClick={decrementDay}
                          class={`text-whitesmoke/40 h-5 w-8 transition-colors ${themeClasses().accentHover}`}
                          aria-label="Decrement day"
                          type="button"
                        >
                          ▼
                        </button>
                        <span class="text-whitesmoke/60 mt-0.5 font-mono text-xs tracking-wider">
                          DAY
                        </span>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div class="flex justify-end gap-2 pt-1">
                      <button
                        onClick={handleCancelDate}
                        class="text-whitesmoke/60 hover:text-whitesmoke/80 border-whitesmoke/10 hover:border-whitesmoke/20 rounded border px-3 py-1 font-mono text-xs transition-colors"
                        type="button"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleResetDate}
                        class={`rounded border px-3 py-1 font-mono text-xs transition-colors ${themeClasses().accentText}/70 ${themeClasses().accentHover} ${themeClasses().border} ${themeClasses().accentBorderHover}`}
                        type="button"
                        title={`Reset to ${resetDateLabel()}`}
                      >
                        Reset
                      </button>
                      <button
                        onClick={handleSaveDate}
                        class={`rounded border px-3 py-1 font-mono text-xs transition-colors ${themeClasses().accentBg} ${themeClasses().accentText} ${themeClasses().accentBgHover} ${themeClasses().accentBorder} ${themeClasses().accentBorderHover}`}
                        type="button"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </Show>
          </div>

          {/* Window dots - clickable if onClose provided, decorative otherwise */}
          {/* NEVER hide or shrink - flex-shrink-0 ensures pips are always visible */}
          <Show
            when={props.onClose}
            fallback={
              <span
                class="text-whitesmoke/15 min-w-fit flex-shrink-0 select-none"
                aria-hidden="true"
              >
                ● ● ●
              </span>
            }
          >
            <button
              onClick={handleClose}
              class="text-whitesmoke/30 hover:text-whitesmoke/80 min-w-fit flex-shrink-0 cursor-pointer transition-colors"
              aria-label="Close panel"
              title="Close"
            >
              ● ● ●
            </button>
          </Show>
        </div>
      </div>

      {/* Content area */}
      <div class="flex min-h-0 flex-1 flex-col">{props.children}</div>
    </div>
  );
};
